import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, TextField, Button, InputLabel,
    InputAdornment, IconButton, Snackbar, Alert,
    CircularProgress, useMediaQuery, useTheme,
    Paper
} from '@mui/material';
import { Visibility, VisibilityOff, ArrowBack } from '@mui/icons-material';
import axios from 'axios';
import appConfig from '../config/appConfig';
import { getReferralCookie, getReferrerIdCookie } from '../utils/referralUtils';
import './LoginPage.css';

const LoginPage = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [tab, setTab] = useState('login'); // 'login' | 'signup'
    const [loading, setLoading] = useState(false);

    // Redirection Logic if already logged in
    useEffect(() => {
        const checkAuthAndRedirect = async () => {
            try {
                const apiUrl = appConfig.API_URL;
                const response = await axios.get(`${apiUrl}/users/check-auth`, { withCredentials: true });

                if (response.data && response.data.id) {
                    const role = response.data.role;
                    if (role === 'User' || role === 'Viewer' || role === 'Admin-Viewer') {
                        window.location.href = `${appConfig.VIEWER_URL}/dashboard`;
                    } else if (role === 'Advertiser' || role === 'Admin-Advertiser') {
                        window.location.href = `${appConfig.ADVERTISER_URL}/dashboard`;
                    }
                    // Do not redirect Admin or Super Admin to ADMIN_URL automatically from the login page
                }
            } catch (error) {
                console.log('User not logged in or session expired');
            }
        };

        checkAuthAndRedirect();
    }, []);

    // Form fields
    const [email, setEmail] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    // Snackbar
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    const showSnack = (message, severity = 'info') => setSnackbar({ open: true, message, severity });
    const closeSnack = () => setSnackbar(s => ({ ...s, open: false }));

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (tab === 'login') {
            if (!email || !password) return showSnack('Please fill in all fields', 'warning');
        } else {
            if (!email || !username || !password || !confirmPassword) return showSnack('Please fill in all fields', 'warning');
            if (password !== confirmPassword) return showSnack('Passwords do not match', 'error');
        }

        setLoading(true);
        try {
            const apiUrl = appConfig.API_URL;

            if (tab === 'login') {
                const response = await axios.post(`${apiUrl}/users/login`, {
                    email,
                    password
                }, { withCredentials: true });

                if (response.data && response.data.id) {
                    showSnack('Login successful!', 'success');
                    const role = response.data.role; // Now we get the role from response
                    // Redirection logic based on role from backend
                    let redirectUrl;
                    if (role === 'Advertiser') {
                        redirectUrl = `${appConfig.ADVERTISER_URL}/dashboard`;
                    } else {
                        redirectUrl = `${appConfig.VIEWER_URL}/dashboard`;
                    }

                    setTimeout(() => {
                        window.location.href = redirectUrl;
                    }, 1000);
                }
            } else {
                // Get referral code from URL or cookie
                const searchParams = new URLSearchParams(window.location.search);
                const refFromUrl = searchParams.get('ref');
                const idFromUrl = searchParams.get('id');
                const referralCode = refFromUrl || getReferralCookie();
                const referrerId = idFromUrl || getReferrerIdCookie();

                // Signup logic 
                const signupResponse = await axios.post(`${apiUrl}/users/viewerSignup`, {
                    email,
                    username,
                    password,
                    confirmPassword,
                    referralCode,
                    referrerId
                }, { withCredentials: true });

                if (signupResponse.data && signupResponse.data.id) {
                    showSnack('Registration successful! Please sign in.', 'success');
                    setTab('login');
                    setPassword('');
                    setConfirmPassword('');
                }
            }
        } catch (error) {
            const msg = error.response?.data?.message || error.response?.data?.developermsg || 'Action failed. Please try again.';
            showSnack(msg, 'error');
        } finally {
            setLoading(false);
        }
    };

    const fieldSx = {
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            borderRadius: '12px',
            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
            '&:hover fieldset': { borderColor: 'rgba(0, 198, 251, 0.4)' },
            '&.Mui-focused fieldset': { borderColor: '#00C6FB' },
        },
        '& .MuiInputBase-input': { color: '#fff' },
        '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.5)' },
    };

    return (
        <Box className="login-container">
            <Box className="login-content">
                <IconButton
                    onClick={() => navigate('/')}
                    className="back-button"
                    sx={{ position: 'absolute', top: 20, left: 20, color: 'rgba(255,255,255,0.6)' }}
                >
                    <ArrowBack />
                </IconButton>

                <Paper elevation={0} className="login-card">
                    <Box className="login-header">
                        <Typography variant="h4" className="brand-logo">TrendAds</Typography>
                        <Box className="tab-selector">
                            <Button
                                className={`tab-btn ${tab === 'login' ? 'active' : ''}`}
                                onClick={() => setTab('login')}
                            >
                                Sign In
                            </Button>
                            <Button
                                className={`tab-btn ${tab === 'signup' ? 'active' : ''}`}
                                onClick={() => setTab('signup')}
                            >
                                Register
                            </Button>
                        </Box>
                        <Typography variant="h5" className="form-title">
                            {tab === 'login' ? 'Welcome Back' : 'Create Account'}
                        </Typography>
                    </Box>

                    <form onSubmit={handleSubmit}>
                        {tab === 'signup' && (
                            <Box className="input-group">
                                <InputLabel className="input-label">Username</InputLabel>
                                <TextField
                                    fullWidth
                                    placeholder="your_username"
                                    value={username}
                                    onChange={e => setUsername(e.target.value)}
                                    sx={fieldSx}
                                />
                            </Box>
                        )}

                        <Box className="input-group">
                            <InputLabel className="input-label">Email Address</InputLabel>
                            <TextField
                                fullWidth
                                placeholder="you@example.com"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                sx={fieldSx}
                            />
                        </Box>

                        <Box className="input-group">
                            <InputLabel className="input-label">Password</InputLabel>
                            <TextField
                                fullWidth
                                type={showPassword ? 'text' : 'password'}
                                placeholder="••••••••"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                sx={fieldSx}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowPassword(!showPassword)} sx={{ color: 'rgba(255,255,255,0.3)' }}>
                                                {showPassword ? <Visibility /> : <VisibilityOff />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Box>

                        {tab === 'signup' && (
                            <Box className="input-group">
                                <InputLabel className="input-label">Confirm Password</InputLabel>
                                <TextField
                                    fullWidth
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={confirmPassword}
                                    onChange={e => setConfirmPassword(e.target.value)}
                                    sx={fieldSx}
                                />
                            </Box>
                        )}

                        <Button
                            fullWidth
                            type="submit"
                            disabled={loading}
                            className="submit-btn"
                            variant="contained"
                        >
                            {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : (tab === 'login' ? 'Sign In' : 'Sign Up')}
                        </Button>
                    </form>

                    <Box className="form-footer">
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                            {tab === 'login'
                                ? "Don't have an account? Switch to Register tab above."
                                : "Already have an account? Switch to Sign In tab above."}
                        </Typography>
                    </Box>
                </Paper>
            </Box>

            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={closeSnack}
            >
                <Alert onClose={closeSnack} severity={snackbar.severity} sx={{ width: '100%', borderRadius: '12px' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default LoginPage;
